import _ from "lodash";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEYS, ETransactionType } from "../helpers/constants";
import { searchTransactions } from "../api/transactions";
import { useCallback, useMemo } from "react";
import useMainFilter from "./useMainFilter";
import { formatCurrency, formatEasyDate } from "../helpers/utils";
import useCurrentAccount from "./useCurrentAccount";
import moment from "moment";
import userPreferencesStore from "../store/userPreferences";

const MAX_DATES = 7;

export default function useFilteredTransactions() {
  const { mainFilter } = useMainFilter();
  const { currencySymbol } = useCurrentAccount();
  const queryClient = useQueryClient();
  const { setSelectedYear, selectedYear } = userPreferencesStore();

  const getParams = useCallback(() => {
    const params = new URLSearchParams();
    const {
      dateFrom,
      dateTo,
      payTo,
      type,
      status,
      paymentMethod,
      tags,
      category,
      account,
    } = mainFilter;

    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    if (payTo?.length) params.append("payTo", JSON.stringify(payTo));
    if (type) params.append("type", type);
    if (category?.length) params.append("category", JSON.stringify(category));
    params.append("account", account);
    if (status?.length) params.append("status", JSON.stringify(status));
    if (paymentMethod?.length)
      params.append("paymentMethod", JSON.stringify(paymentMethod));
    if (tags?.length) params.append("tags", JSON.stringify(tags));
    params.append("deleted", "false");
    params.append("archived", "false");
    params.append("shared", "false");

    return params;
  }, [mainFilter]);

  const queryKey = [
    CACHE_KEYS.FILTERED_TRANSACTIONS,
    JSON.stringify(mainFilter),
  ];

  const {
    data: filteredTransactions,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (mainFilter?.account) {
        return await searchTransactions({
          fieldsToSearch: getParams(),
        });
      }
      return { data: { data: [] } };
    },
    enabled: !!mainFilter?.account, // Evitar la consulta si no hay cuenta seleccionada
  });

  // Obtener las fechas únicas de las últimas transacciones
  const lastDates = useMemo(() => {
    return _.take(
      _.uniq(filteredTransactions?.data?.map((t: any) => t.date)),
      MAX_DATES
    );
  }, [filteredTransactions?.data]);

  const latestTransactions = useMemo(() => {
    return _.filter(filteredTransactions?.data, (transaction) =>
      lastDates?.includes(transaction?.date)
    );
  }, [filteredTransactions?.data, lastDates]);

  const latestTransactionsSummary = useMemo(() => {
    // Agrupar transacciones por fecha
    const groupedByDate = _.groupBy(latestTransactions, "date");

    return Object.keys(groupedByDate).map((date) => {
      const transactions = groupedByDate[date];
      const income = _.sumBy(
        transactions.filter((t: any) => t.type === ETransactionType.INCOME),
        (t: any) => parseFloat(t.total) || 0
      );
      const expenses = _.sumBy(
        transactions.filter((t: any) => t.type === ETransactionType.EXPENSE),
        (t: any) => parseFloat(t.total) || 0
      );

      return {
        date,
        income,
        expenses,
        formattedDate: formatEasyDate({ dateToFormat: date }),
        formattedIncome: formatCurrency({
          value: income,
          currency: currencySymbol,
        }),
        formattedExpenses: formatCurrency({
          value: expenses,
          currency: currencySymbol,
        }),
        difference: income - expenses,
        differenceFormatted: formatCurrency({
          value: income - expenses,
          currency: currencySymbol,
        }),
      };
    });
  }, [latestTransactions, currencySymbol]);

  const groupedByMonthAndYear = useMemo(() => {
    return _(filteredTransactions?.data)
      .groupBy((t: any) =>
        moment(t.date).format(selectedYear ? "YYYY-MM" : "MM")
      )
      .mapValues((entries) =>
        _.mapValues(_.groupBy(entries, "type"), (items) =>
          _.sumBy(items, (item) => Number(item.total))
        )
      )
      .value();
  }, [filteredTransactions?.data, selectedYear]);

  const transactionsFilteredByYear = useMemo(() => {
    if (selectedYear) {
      return (
        filteredTransactions?.data?.filter?.((transaction: any) => {
          return (
            moment(transaction?.date)?.format("YYYY") === `${selectedYear}`
          );
        }) || []
      );
    }

    return filteredTransactions?.data;
  }, [filteredTransactions?.data, selectedYear]);

  const uniqueYears = useMemo(() => {
    const res = _.uniq(
      filteredTransactions?.data?.map((t: any) => moment(t.date).year())
    );

    if (!selectedYear || !res?.includes?.(selectedYear)) {
      if (selectedYear && !res?.includes?.(selectedYear)) {
        setSelectedYear("");
      }
      // if (res?.length) {
      //   setSelectedYear(res?.[0] as string);
      // }
    }

    return res;
  }, [filteredTransactions?.data]);

  const getFilteredTransactionById = ({
    transactionId,
  }: {
    transactionId: string;
  }) => {
    return _.find(filteredTransactions?.data || [], { invoice: transactionId });
  };

  const refetchFilteredQueries = async () => {
    return await Promise.allSettled([queryClient.refetchQueries({ queryKey })]);
  };

  return {
    filteredTransactions: filteredTransactions?.data,
    isFilteringTransactions: isLoading,
    refetchFilteredQueries,
    latestTransactionsSummary,
    monthlyTransactionsSummary: groupedByMonthAndYear,
    uniqueYears,
    getFilteredTransactionById,
    transactionsFilteredByYear,
    isRefetchingFilteredTransactions: isRefetching,
  };
}
